import React from 'react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import './InternationalPhoneInput.scss';

// Utility function to ensure phone number is in E.164 format
const formatPhoneForInput = (phone: string | undefined): string | undefined => {
  if (!phone) return undefined;
  
  // If already has + prefix, return as is
  if (phone.startsWith('+')) {
    return phone;
  }
  
  // If starts with country code (like 880 for Bangladesh), add + prefix
  if (phone.match(/^[1-9]\d{1,14}$/)) {
    return `+${phone}`;
  }
  
  // For other formats, return as is (let the library handle it)
  return phone;
};

// Utility function to format phone for storage (remove + prefix)
const formatPhoneForStorage = (phone: string | undefined): string => {
  if (!phone) return '';
  
  // Remove + prefix if present
  return phone.startsWith('+') ? phone.substring(1) : phone;
};

interface InternationalPhoneInputProps {
  value?: string;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  id?: string;
  name?: string;
  error?: string;
  label?: string;
  showLabel?: boolean;
}

const InternationalPhoneInput: React.FC<InternationalPhoneInputProps> = ({
  value,
  onChange,
  placeholder = "Enter phone number",
  disabled = false,
  required = false,
  className = "",
  id,
  name,
  error,
  label,
  showLabel = true
}) => {
  // Format the value for the phone input library (ensure E.164 format)
  const formattedValue = formatPhoneForInput(value);
  
  // Handle onChange to format the value for storage (remove + prefix)
  const handleChange = (newValue: string | undefined) => {
    const formattedForStorage = formatPhoneForStorage(newValue);
    onChange(formattedForStorage);
  };
  
  
  return (
    <div className={`international-phone-input ${className}`}>
      {showLabel && label && (
        <label htmlFor={id} className="phone-input-label">
          <i className="fas fa-phone me-2"></i>
          {label}
          {required && <span className="text-danger ms-1">*</span>}
        </label>
      )}
      
      <PhoneInput
        international
        countryCallingCodeEditable={false}
        defaultCountry="BD" // Default to Bangladesh, can be made configurable
        value={formattedValue}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        id={id}
        name={name}
        className={`phone-input ${error ? 'is-invalid' : ''}`}
        inputComponent={CustomInput}
        format={(value: string | undefined, country: any) => {
          // Remove + sign and format as requested (e.g., 880 for BD)
          if (!value) return '';
          const countryCode = country?.callingCode || '';
          const nationalNumber = value.replace(`+${countryCode}`, '').trim();
          return `${countryCode}${nationalNumber}`;
        }}
      />
      
      {error && (
        <div className="invalid-feedback">
          {error}
        </div>
      )}
    </div>
  );
};

// Custom input component to match our styling
const CustomInput: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => {
  return (
    <input
      {...props}
      className={`form-control ${props.className || ''}`}
    />
  );
};

export default InternationalPhoneInput;
